/**
 * BeatDuel analytics module — tracks page views and user interactions.
 *
 * Events are sent to /events/track (ClickHouse only).
 * All tracking is fire-and-forget — failures don't affect the app.
 */

import { api } from '$shared/api';

// Session ID — persists across SPA navigation, cleared on tab close
const SESSION_KEY = 'beatduel_session_id';
let sessionId = sessionStorage.getItem(SESSION_KEY);
if (!sessionId) {
  // crypto.randomUUID() requires a secure context (HTTPS or localhost).
  // Fall back to manual UUID for non-secure contexts (e.g. E2E tests over plain HTTP).
  sessionId =
    typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
  sessionStorage.setItem(SESSION_KEY, sessionId);
}

/**
 * Track a general user event (page view, search, share, etc.).
 * Fire-and-forget — no return value needed.
 */
export function trackEvent(eventType: string, data: Record<string, unknown> = {}) {
  const { target_id, target_type, metadata, referrer, ...rest } = data;

  api
    .post('/events/track', {
      event_type: eventType,
      page: window.location.hash.replace('#', '') || '/',
      target_id: target_id ?? 0,
      target_type: target_type ?? '',
      metadata: metadata ?? rest,
      session_id: sessionId,
      referrer: referrer ?? document.referrer,
      platform: 'web',
    })
    .catch(() => {}); // fire-and-forget
}

/**
 * Track a page view. Called automatically by the router on hash changes.
 */
export function trackPageView(path: string) {
  trackEvent('page_view', { metadata: { path } });
}

/** Track a profile view. */
export function trackProfileView(userId: number) {
  trackEvent('profile_view', { target_id: userId, target_type: 'user' });
}

/** Track a notification click with the notification type. */
export function trackNotificationClick(notificationId: number, notificationType: string) {
  trackEvent('notification_click', {
    target_id: notificationId,
    target_type: 'notification',
    metadata: { notification_type: notificationType },
  });
}

/** Track a settings change. */
export function trackSettingsChange(field: string) {
  trackEvent('settings_change', { metadata: { field } });
}

/** Track an invite send. */
export function trackInviteSend() {
  trackEvent('invite_send');
}

/** Track onboarding style selection. */
export function trackOnboardingStyles(styles: string[]) {
  trackEvent('onboarding_styles_selected', { metadata: { styles, count: styles.length } });
}

/** Track onboarding completion. */
export function trackOnboardingComplete() {
  trackEvent('onboarding_complete');
}
